import React from "react";

import {
  WebSettingsContext,
  WebSettingsSections,
  AccountSections,
  TeamsSections,
  TeamSections,
  OrganizationSections,
  InternalSections,
  HoursAndPreferencesSections,
  SmartHoldsSections,
  IntegrationsSections,
  getExpandableTreeItemFromViewId,
  maybeGetTeamIdFromViewId,
  createSettingsViewId,
} from "../web-settings";

// util
// import { getCurrentOrg } from "@src/util/org.util";
// import { logger } from "@src/util/logger.util";
import { canAccessExperiments } from "@util/auth.util";

// material-ui
import {
  AccessTime,
  AddCircle,
  Assistant,
  BarChart,
  CalendarToday,
  CreditCard,
  DateRange,
  Flag,
  // GroupWork,
  LockOutlined,
  Menu,
  NotInterested,
  Notifications,
  SettingsApplications,
  SwapHoriz,
  People,
} from "@material-ui/icons";
import { Box, IconButton, Typography } from "@material-ui/core";
import { TreeView, TreeItem } from "@material-ui/lab";
import { makeStyles } from "@material-ui/styles";

import { greys, colors, helpers } from "@clockwise/web-commons/src/styles/color.styles";

//////////////////
// Web Settings Tree Item Component
//////////////////
type WebSettingsTreeItemProps = {
  settingsViewId: string;
  label: React.ReactNode;
  onClick: (settingsViewId: string) => void;
  icon?: React.ReactNode;
  children?: React.ReactNode;
  locked?: boolean;
};

const useWebSettingsTreeItemStyles = makeStyles({
  "@global": {
    ".MuiTreeItem-root > .MuiTreeItem-content .MuiTreeItem-label": {
      color: greys.standard,
    },
    ".MuiTreeItem-root > .MuiTreeItem-group .MuiTreeItem-content .MuiTreeItem-label": {
      color: greys.blueGrey,
    },
    ".MuiTreeItem-root.Mui-selected > .MuiTreeItem-content .MuiTreeItem-label": {
      backgroundColor: "transparent",
      color: colors.blueDusk.darker,
    },
    ".MuiTreeItem-root.Mui-selected > .MuiTreeItem-content .MuiTreeItem-label:hover, .MuiTreeItem-root.Mui-selected:focus > .MuiTreeItem-content .MuiTreeItem-label": {
      backgroundColor: "transparent",
    },
    ".MuiTreeItem-root > .MuiTreeItem-content .MuiTreeItem-label:hover, .MuiTreeItem-root:focus > .MuiTreeItem-content .MuiTreeItem-label": {
      backgroundColor: "transparent",
    },
  },
  group: {
    margin: "5px 0",
  },
  label: {
    display: "flex",
    alignItems: "center",
    padding: "6px 0",
  },
  content: {
    padding: "0 5px 0 30px",
  },
  bold: {
    fontWeight: 700,
  },
  selectedContent: {
    background: helpers.opacity(colors.blueCool.lighter, 0.2),
  },
  root: {},
  verticalLine: {
    height: 36,
    borderLeft: `2px solid ${greys.borderGrey}`,
    display: "flex",
    "& > .selected": {
      alignSelf: "center",
      height: 28,
      width: 2,
      marginLeft: -2,
      background: colors.blueDusk.darker,
    },
  },
  icon: {
    marginRight: 16,
    color: greys.blueGrey,
  },
  iconSelected: {
    color: colors.blueDusk.darker,
  },
  lockIcon: {
    color: "#DADADA",
    width: 15,
    marginLeft: 15,
  },
  disabled: {
    pointerEvents: "none",
  },
});

function WebSettingsTreeItem(props: WebSettingsTreeItemProps) {
  const classes = useWebSettingsTreeItemStyles();
  const context = React.useContext(WebSettingsContext);

  const isSelected = props.settingsViewId === context.settingsViewId;
  const isSelectedParent =
    props.settingsViewId ===
    createSettingsViewId(
      getExpandableTreeItemFromViewId(context.settingsViewId),
      maybeGetTeamIdFromViewId(context.settingsViewId) || undefined,
    );

  return (
    <TreeItem
      nodeId={props.settingsViewId}
      label={
        <>
          {props.label}
          {props.locked ? <LockOutlined className={classes.lockIcon} /> : ""}
        </>
      }
      onClick={() => props.onClick(props.settingsViewId)}
      icon={
        props.icon || (
          <div className={classes.verticalLine}>{isSelected && <div className="selected" />}</div>
        )
      }
      classes={{
        root: `${classes.root} ${props.locked && classes.disabled}`,
        content: `${classes.content} ${isSelectedParent && classes.selectedContent}`,
        label: `${classes.label} ${isSelectedParent && classes.bold}`,
        group: `${classes.group}`,
        iconContainer: `${classes.icon} ${isSelectedParent && classes.iconSelected}`,
      }}
    >
      {props.children}
    </TreeItem>
  );
}

//////////////////
// Web Settings Drawer Component
//////////////////
type WebSettingsDrawerProps = {
  onSelect: (settingsViewId: string) => void;
  teamsMenuList: Array<{
    teamName: string;
    teamId: string;
    isPrimary: boolean;
  }>;
};

const animationTime = "350ms";

const useWebSettingsDrawerStyles = makeStyles({
  container: {
    width: 300,
    height: "100%",
    borderRight: `1px solid ${greys.borderGrey}`,
    overflowY: "auto",
    overflowX: "hidden",
    flexShrink: 0,
    paddingBottom: 30,
    transition: `all ${animationTime} ease-in-out`,
    "&.collapsed": {
      overflowY: "hidden",
      width: 48,
      overflow: "hidden",
    },
    "@media (max-width: 768px)": {
      position: "absolute",
      zIndex: 1,
    },
  },
  box: {
    flexGrow: 1,
    animation: `$show-content ${animationTime} 1 ease-in-out forwards`,
    "&.hidden": {
      animation: `$hide-content ${animationTime} 1 ease-in-out forwards`,
    },
  },
  header: {
    fontWeight: "bold",
    padding: "25px 5px 25px 30px",
    color: greys.standard,
  },
  "@keyframes hide-content": {
    "0%": {
      opacity: 1,
    },
    "50%": {
      opacity: 0,
    },
    "100%": {
      opacity: 0,
      pointerEvents: "none",
    },
  },
  "@keyframes show-content": {
    "0%": {
      opacity: 0,
    },
    "50%": {
      opacity: 1,
    },
    "100%": {
      opacity: 1,
    },
  },
  ellipsis: {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: 150,
  },
  primaryBadge: {
    textTransform: "uppercase",
    color: greys.white,
    background: colors.blueCool.standard,
    borderRadius: 3,
    fontSize: 12,
    fontWeight: "bold",
    marginLeft: 10,
    padding: "2px 5px",
    width: "fit-content",
  },
});

export function WebSettingsDrawer(props: WebSettingsDrawerProps) {
  const classes = useWebSettingsDrawerStyles();
  const context = React.useContext(WebSettingsContext);

  const isMobile = window.matchMedia("(max-width: 768px)").matches;

  // ~-~-~-~-~-~-~-
  // State
  // ~-~-~-~-~-~-~-
  const [drawerExpanded, setDrawerExpanded] = React.useState(!isMobile);
  const [expandedTreeItem, setExpandedTreeItem] = React.useState<string>(
    getExpandableTreeItemFromViewId(context.settingsViewId),
  );

  React.useEffect(() => {
    const maybeParentTreeItem = getExpandableTreeItemFromViewId(context.settingsViewId);
    const maybeTeamId = maybeGetTeamIdFromViewId(context.settingsViewId) || undefined;
    setExpandedTreeItem(createSettingsViewId(maybeParentTreeItem, maybeTeamId));
  }, [context.settingsViewId]);

  // ~-~-~-~-~-~-~-
  // Click Handlers
  // ~-~-~-~-~-~-~-s
  const onClickTreeItem = (settingsViewId: string) => {
    props.onSelect(settingsViewId);
  };

  const onClickToggleDrawerExpansion = () => {
    setDrawerExpanded(!drawerExpanded);
  };

  // ~-~-~-~-~-~-~-
  // Render
  // ~-~-~-~-~-~-~-
  return (
    <div className={`${!drawerExpanded ? "collapsed" : ""} ${classes.container}`}>
      <div className="cw-relative cw-bg-white">
        <div
          className="cw-flex cw-items-center cw-justify-end cw-w-full cw-absolute cw-z-10"
          style={{ paddingTop: 11 }}
        >
          <IconButton onClick={onClickToggleDrawerExpansion}>
            <Menu />
          </IconButton>
        </div>
        <Box className={`${classes.box} ${!drawerExpanded ? "hidden" : ""}`}>
          <TreeView
            aria-label="controlled"
            expanded={[expandedTreeItem]}
            selected={[context.settingsViewId]}
          >
            <Typography variant="body1" className={classes.header}>
              Account
            </Typography>
            <WebSettingsTreeItem
              settingsViewId={createSettingsViewId(
                WebSettingsSections.Account,
                AccountSections.HoursAndPreferences,
              )}
              label="Hours and preferences"
              onClick={onClickTreeItem}
              icon={<AccessTime />}
            >
              <WebSettingsTreeItem
                settingsViewId={createSettingsViewId(
                  WebSettingsSections.Account,
                  AccountSections.HoursAndPreferences,
                  HoursAndPreferencesSections.WorkingHours,
                )}
                label="Working hours"
                onClick={onClickTreeItem}
              />
              <WebSettingsTreeItem
                settingsViewId={createSettingsViewId(
                  WebSettingsSections.Account,
                  AccountSections.HoursAndPreferences,
                  HoursAndPreferencesSections.MeetingHours,
                )}
                label="Meeting hours"
                onClick={onClickTreeItem}
              />
              <WebSettingsTreeItem
                settingsViewId={createSettingsViewId(
                  WebSettingsSections.Account,
                  AccountSections.HoursAndPreferences,
                  HoursAndPreferencesSections.TimeZone,
                )}
                label="Time zone"
                onClick={onClickTreeItem}
              />
              <WebSettingsTreeItem
                settingsViewId={createSettingsViewId(
                  WebSettingsSections.Account,
                  AccountSections.HoursAndPreferences,
                  HoursAndPreferencesSections.MeetingBreak,
                )}
                label="Meeting break"
                onClick={onClickTreeItem}
              />
              <WebSettingsTreeItem
                settingsViewId={createSettingsViewId(
                  WebSettingsSections.Account,
                  AccountSections.HoursAndPreferences,
                  HoursAndPreferencesSections.TimeOfDayPreference,
                )}
                label="Time of day preference"
                onClick={onClickTreeItem}
              />
              <WebSettingsTreeItem
                settingsViewId={createSettingsViewId(
                  WebSettingsSections.Account,
                  AccountSections.HoursAndPreferences,
                  HoursAndPreferencesSections.PreferredMeetingRooms,
                )}
                label="Preferred meeting rooms"
                onClick={onClickTreeItem}
              />
            </WebSettingsTreeItem>
            <WebSettingsTreeItem
              settingsViewId={createSettingsViewId(
                WebSettingsSections.Account,
                AccountSections.SmartHolds,
              )}
              label="Smart Holds"
              onClick={onClickTreeItem}
              icon={<Assistant />}
            >
              <WebSettingsTreeItem
                settingsViewId={createSettingsViewId(
                  WebSettingsSections.Account,
                  AccountSections.SmartHolds,
                  SmartHoldsSections.FocusTime,
                )}
                label="Focus Time"
                onClick={onClickTreeItem}
              />
              <WebSettingsTreeItem
                settingsViewId={createSettingsViewId(
                  WebSettingsSections.Account,
                  AccountSections.SmartHolds,
                  SmartHoldsSections.LunchHold,
                )}
                label="Lunch hold"
                onClick={onClickTreeItem}
              />
              <WebSettingsTreeItem
                settingsViewId={createSettingsViewId(
                  WebSettingsSections.Account,
                  AccountSections.SmartHolds,
                  SmartHoldsSections.TravelTime,
                )}
                label="Travel time"
                onClick={onClickTreeItem}
              />
              <WebSettingsTreeItem
                settingsViewId={createSettingsViewId(
                  WebSettingsSections.Account,
                  AccountSections.SmartHolds,
                  SmartHoldsSections.PersonalCalendarHold,
                )}
                label="Personal calendar hold"
                onClick={onClickTreeItem}
              />
            </WebSettingsTreeItem>
            <WebSettingsTreeItem
              settingsViewId={createSettingsViewId(
                WebSettingsSections.Account,
                AccountSections.Integrations,
              )}
              label="Integrations"
              onClick={onClickTreeItem}
              icon={<SwapHoriz />}
            >
              <WebSettingsTreeItem
                settingsViewId={createSettingsViewId(
                  WebSettingsSections.Account,
                  AccountSections.Integrations,
                  IntegrationsSections.Slack,
                )}
                label="Slack"
                onClick={onClickTreeItem}
              />
              <WebSettingsTreeItem
                settingsViewId={createSettingsViewId(
                  WebSettingsSections.Account,
                  AccountSections.Integrations,
                  IntegrationsSections.Zoom,
                )}
                label="Zoom"
                onClick={onClickTreeItem}
              />
              <WebSettingsTreeItem
                settingsViewId={createSettingsViewId(
                  WebSettingsSections.Account,
                  AccountSections.Integrations,
                  IntegrationsSections.Asana,
                )}
                label="Asana"
                onClick={onClickTreeItem}
              />
            </WebSettingsTreeItem>
            <WebSettingsTreeItem
              settingsViewId={createSettingsViewId(
                WebSettingsSections.Account,
                AccountSections.EventColorCoding,
              )}
              label="Event color coding"
              onClick={onClickTreeItem}
              icon={<DateRange />}
            />
            <WebSettingsTreeItem
              settingsViewId={createSettingsViewId(
                WebSettingsSections.Account,
                AccountSections.TeamAvailabilityCalendar,
              )}
              label="Team availability calendar"
              onClick={onClickTreeItem}
              icon={<CalendarToday />}
            />
            <WebSettingsTreeItem
              settingsViewId={createSettingsViewId(
                WebSettingsSections.Account,
                AccountSections.TeamNoMeetingDay,
              )}
              label="Team no-meeting day"
              onClick={onClickTreeItem}
              icon={<NotInterested />}
            />
            <WebSettingsTreeItem
              settingsViewId={createSettingsViewId(
                WebSettingsSections.Account,
                AccountSections.EmailsAndNotifications,
              )}
              label="Emails and notifications"
              onClick={onClickTreeItem}
              icon={<Notifications />}
            />
            <Typography variant="body1" className={classes.header}>
              Teams
            </Typography>
            {props.teamsMenuList.map((team) => {
              return (
                <WebSettingsTreeItem
                  key={`web-settings-tree-item-team-${team.teamId}`}
                  settingsViewId={createSettingsViewId(
                    WebSettingsSections.Teams,
                    TeamsSections.Team,
                    team.teamId,
                  )}
                  label={
                    <>
                      <div className={classes.ellipsis}>{team.teamName}</div>
                      {team.isPrimary && <div className={classes.primaryBadge}>Primary</div>}
                    </>
                  }
                  onClick={onClickTreeItem}
                  icon={<People />}
                >
                  <WebSettingsTreeItem
                    settingsViewId={createSettingsViewId(
                      WebSettingsSections.Teams,
                      TeamsSections.Team,
                      team.teamId,
                      TeamSections.TeamName,
                    )}
                    label="Team name"
                    onClick={onClickTreeItem}
                  />
                  <WebSettingsTreeItem
                    settingsViewId={createSettingsViewId(
                      WebSettingsSections.Teams,
                      TeamsSections.Team,
                      team.teamId,
                      TeamSections.PlanDetails,
                    )}
                    label="Plan details"
                    onClick={onClickTreeItem}
                  />
                  <WebSettingsTreeItem
                    settingsViewId={createSettingsViewId(
                      WebSettingsSections.Teams,
                      TeamsSections.Team,
                      team.teamId,
                      TeamSections.Members,
                    )}
                    label="Members"
                    onClick={onClickTreeItem}
                  />
                  <WebSettingsTreeItem
                    settingsViewId={createSettingsViewId(
                      WebSettingsSections.Teams,
                      TeamsSections.Team,
                      team.teamId,
                      TeamSections.TeamAvailabilityCalendar,
                    )}
                    label="Team availability calendar"
                    onClick={onClickTreeItem}
                  />
                  <WebSettingsTreeItem
                    settingsViewId={createSettingsViewId(
                      WebSettingsSections.Teams,
                      TeamsSections.Team,
                      team.teamId,
                      TeamSections.TeamNoMeetingDay,
                    )}
                    label="Team no-meeting day"
                    onClick={onClickTreeItem}
                  />
                </WebSettingsTreeItem>
              );
            })}
            <WebSettingsTreeItem
              settingsViewId={createSettingsViewId(
                WebSettingsSections.Teams,
                TeamsSections.CreateOrJoinTeam,
              )}
              label="Create or join team"
              onClick={onClickTreeItem}
              icon={<AddCircle />}
            />
            <Typography variant="body1" className={classes.header}>
              Organization
            </Typography>
            <WebSettingsTreeItem
              settingsViewId={createSettingsViewId(
                WebSettingsSections.Organization,
                OrganizationSections.Analytics,
              )}
              label="Analytics"
              onClick={onClickTreeItem}
              icon={<BarChart />}
            />
            <WebSettingsTreeItem
              settingsViewId={createSettingsViewId(
                WebSettingsSections.Organization,
                OrganizationSections.Members,
              )}
              label="Members"
              onClick={onClickTreeItem}
              icon={<People />}
            />
            {/* <WebSettingsTreeItem
              settingsViewId={createSettingsViewId(
                WebSettingsSections.Organization,
                OrganizationSections.Teams,
              )}
              label="Teams"
              onClick={onClickTreeItem}
              icon={<GroupWork />}
              locked
            /> */}
            <WebSettingsTreeItem
              settingsViewId={createSettingsViewId(
                WebSettingsSections.Organization,
                OrganizationSections.PlansAndBilling,
              )}
              label="Plans and billing"
              onClick={onClickTreeItem}
              icon={<CreditCard />}
            />
            <WebSettingsTreeItem
              settingsViewId={createSettingsViewId(
                WebSettingsSections.Organization,
                OrganizationSections.OrganizationSettings,
              )}
              label="Organization settings"
              onClick={onClickTreeItem}
              icon={<SettingsApplications />}
            />
            {canAccessExperiments() ? (
              <>
                <Typography variant="body1" className={classes.header}>
                  Internal
                </Typography>
                <WebSettingsTreeItem
                  settingsViewId={createSettingsViewId(
                    WebSettingsSections.Internal,
                    InternalSections.Experiments,
                  )}
                  label="Experiments"
                  onClick={onClickTreeItem}
                  icon={<Flag />}
                />
              </>
            ) : (
              ""
            )}
          </TreeView>
        </Box>
      </div>
    </div>
  );
}
